/**
 * GDPR Cookie Consent Management
 * Handles cookie consent banner and preference management
 */

class GDPRConsent {
  constructor() {
    this.consentKey = 'gdpr_consent';
    this.consentExpiry = 365; // days
    this.init();
  }

  init() {
    this.bindEvents();
    this.checkConsentStatus();
  }

  bindEvents() {
    // Accept all cookies
    document.addEventListener('click', (e) => {
      if (e.target.id === 'gdpr-accept-all' || e.target.id === 'gdpr-accept-all-modal') {
        this.acceptAllCookies();
      }
      
      // Manage preferences
      if (e.target.id === 'gdpr-manage-preferences') {
        this.showPreferencesModal();
      }
      
      // Save preferences
      if (e.target.id === 'gdpr-save-preferences') {
        this.savePreferences();
      }
      
      // Close modal
      if (e.target.id === 'gdpr-close-modal') {
        this.hidePreferencesModal();
      }
    });

    // Close modal when clicking outside
    document.addEventListener('click', (e) => {
      const modal = document.getElementById('gdpr-preferences-modal');
      if (e.target === modal) {
        this.hidePreferencesModal();
      }
    });
  }

  checkConsentStatus() {
    const consent = this.getConsent();
    
    if (!consent || this.isConsentExpired(consent)) {
      this.showBanner();
      // Track dismissal when user navigates away
      this.setupDismissalTracking();
    } else {
      this.hideBanner();
      this.loadConsentedScripts(consent);
    }
  }
  
  setupDismissalTracking() {
    // Track if user leaves page without accepting
    let dismissalTracked = false;
    
    const trackOnExit = () => {
      if (!dismissalTracked && document.getElementById('gdpr-cookie-banner')?.style.display !== 'none') {
        dismissalTracked = true;
        this.trackDismissal();
      }
    };
    
    // Track on page unload
    window.addEventListener('beforeunload', trackOnExit);
    
    // Track if user starts navigating/clicking around without accepting
    setTimeout(() => {
      document.addEventListener('click', (e) => {
        // If clicking outside the banner and modal
        const banner = document.getElementById('gdpr-cookie-banner');
        const modal = document.getElementById('gdpr-preferences-modal');
        
        if (!dismissalTracked && 
            banner?.style.display !== 'none' && 
            !banner?.contains(e.target) && 
            !modal?.contains(e.target)) {
          dismissalTracked = true;
          this.trackDismissal();
        }
      }, { once: true });
    }, 1000); // Delay to avoid immediate tracking
  }

  showBanner() {
    const banner = document.getElementById('gdpr-cookie-banner');
    if (banner) {
      banner.style.display = 'block';
    }
  }

  hideBanner() {
    const banner = document.getElementById('gdpr-cookie-banner');
    if (banner) {
      banner.style.display = 'none';
    }
  }

  showPreferencesModal() {
    const modal = document.getElementById('gdpr-preferences-modal');
    if (modal) {
      // Load current preferences
      const consent = this.getConsent();
      if (consent && consent.preferences) {
        document.getElementById('gdpr-analytics').checked = consent.preferences.analytics || false;
        document.getElementById('gdpr-marketing').checked = consent.preferences.marketing || false;
      }
      modal.style.display = 'block';
      document.body.style.overflow = 'hidden';
    }
  }

  hidePreferencesModal() {
    const modal = document.getElementById('gdpr-preferences-modal');
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = '';
    }
  }

  acceptAllCookies() {
    const consent = {
      timestamp: Date.now(),
      preferences: {
        essential: true,
        analytics: true,
        marketing: true
      }
    };
    
    this.saveConsent(consent);
    this.hideBanner();
    this.hidePreferencesModal();
    this.loadConsentedScripts(consent);
    
    // Track consent with Ahoy
    this.trackConsentToBackend('accepted_all', true, true);
  }

  savePreferences() {
    const analyticsConsent = document.getElementById('gdpr-analytics').checked;
    const marketingConsent = document.getElementById('gdpr-marketing').checked;
    
    const consent = {
      timestamp: Date.now(),
      preferences: {
        essential: true, // Always true
        analytics: analyticsConsent,
        marketing: marketingConsent
      }
    };
    
    this.saveConsent(consent);
    this.hideBanner();
    this.hidePreferencesModal();
    this.loadConsentedScripts(consent);
    
    // Track consent with Ahoy
    this.trackConsentToBackend('custom_preferences', analyticsConsent, marketingConsent);
  }

  saveConsent(consent) {
    localStorage.setItem(this.consentKey, JSON.stringify(consent));
  }

  getConsent() {
    try {
      const consent = localStorage.getItem(this.consentKey);
      return consent ? JSON.parse(consent) : null;
    } catch (e) {
      return null;
    }
  }

  isConsentExpired(consent) {
    if (!consent || !consent.timestamp) return true;
    
    const expiryTime = consent.timestamp + (this.consentExpiry * 24 * 60 * 60 * 1000);
    return Date.now() > expiryTime;
  }

  loadConsentedScripts(consent) {
    // Analytics scripts (Google Analytics, Amplitude)
    if (consent.preferences.analytics) {
      this.loadAnalyticsScripts();
    }
    
    // Marketing scripts (Facebook Pixel, GTM)
    if (consent.preferences.marketing) {
      this.loadMarketingScripts();
    }
    
    // Trigger consent change event for other scripts to listen
    document.dispatchEvent(new CustomEvent('gdprConsentChanged', { 
      detail: consent.preferences 
    }));
  }

  loadAnalyticsScripts() {
    // Flag for other scripts to check
    window.gdprAnalyticsConsent = true;
    
    // Trigger amplitude if already loaded
    if (window.amplitude && typeof window.amplitude.init === 'function') {
      console.log('GDPR: Analytics consent given - Amplitude active');
    }
    
    // Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
      console.log('GDPR: Analytics consent given - GA active');
    }
  }

  loadMarketingScripts() {
    // Flag for other scripts to check
    window.gdprMarketingConsent = true;
    
    // Facebook Pixel
    if (window.fbq) {
      console.log('GDPR: Marketing consent given - Facebook Pixel active');
    }
    
    // Google Analytics marketing features
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted'
      });
    }
  }

  // Public method to check consent for specific category
  hasConsent(category) {
    const consent = this.getConsent();
    if (!consent || this.isConsentExpired(consent)) {
      return false;
    }
    return consent.preferences[category] === true;
  }

  // Public method to revoke consent
  revokeConsent() {
    localStorage.removeItem(this.consentKey);
    window.gdprAnalyticsConsent = false;
    window.gdprMarketingConsent = false;
    this.showBanner();
  }
  
  // Track consent choice to backend using Ahoy
  trackConsentToBackend(action, analyticsConsent, marketingConsent) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    
    fetch('/api/gdpr/track_consent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        consent_action: action,
        analytics_consent: analyticsConsent,
        marketing_consent: marketingConsent
      })
    })
    .then(response => response.json())
    .then(data => {
      console.log('GDPR consent tracked:', data);
    })
    .catch(error => {
      console.error('Failed to track GDPR consent:', error);
    });
  }
  
  // Track banner dismissal (user closed/navigated without accepting)
  trackDismissal() {
    // Only track if banner was shown and no consent exists
    const consent = this.getConsent();
    if (!consent || this.isConsentExpired(consent)) {
      this.trackConsentToBackend('dismissed', false, false);
    }
  }
}

// Initialize GDPR consent management when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  window.gdprConsent = new GDPRConsent();
});

// Make consent checker available globally
window.hasGDPRConsent = function(category) {
  return window.gdprConsent ? window.gdprConsent.hasConsent(category) : false;
};

// Export for module use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = GDPRConsent;
}